// Agent management API
import { api } from './client';

export interface CreateAgentRequest {
  name: string;
  description?: string;
  allowed_cidrs?: string[];
  denied_cidrs?: string[];
  max_concurrent?: number;
  project_id?: number;
  organization_id?: number;
}

export interface UpdateAgentRequest {
  name?: string;
  description?: string;
  allowed_cidrs?: string[];
  denied_cidrs?: string[];
  max_concurrent?: number;
  is_active?: boolean;
  project_id?: number;
}

export interface AgentCommandRequest {
  type: string;
  params?: Record<string, any>;
}

export interface AgentScanRequest {
  targets: string[];
  scan_type?: string;
  options?: Record<string, string>;
  timeout?: number;
}

export const agentsAPI = {
  getAll: (params?: { status?: string; active?: boolean }) => {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append('status', params.status);
    if (params?.active !== undefined) queryParams.append('active', String(params.active));
    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    console.log('🤖 Fetching all agents', queryString);
    return api.get(`/agents${queryString}`);
  },
  // Lists the agent binaries actually present on the server, including the real served
  // version per OS/arch and the overall latest_version — so the UI shows what a download
  // truly gets rather than a hardcoded constant.
  getAvailableBinaries: () => {
    return api.get('/agents/download');
  },
  getById: (agentId: string) => {
    console.log('🤖 Fetching agent:', agentId);
    return api.get(`/agents/${agentId}`);
  },
  getByProject: (projectId: number) => {
    console.log('🤖 Fetching agents for project:', projectId);
    return api.get(`/projects/${projectId}/agents`);
  },
  create: (data: CreateAgentRequest) => {
    console.log('➕ Creating new agent:', data.name);
    return api.post('/agents', data);
  },
  update: (agentId: string, data: UpdateAgentRequest) => {
    console.log('📝 Updating agent:', agentId, data);
    return api.put(`/agents/${agentId}`, data);
  },
  delete: (agentId: string) => {
    console.log('🗑️ Deleting agent:', agentId);
    return api.delete(`/agents/${agentId}`);
  },
  sendCommand: (agentId: string, data: AgentCommandRequest) => {
    console.log('📤 Sending command to agent:', agentId, data);
    return api.post(`/agents/${agentId}/commands`, data);
  },
  getCommands: (agentId: string, limit: number = 50) => {
    console.log('📋 Fetching commands for agent:', agentId);
    return api.get(`/agents/${agentId}/commands?limit=${limit}`);
  },
  createScan: (agentId: string, data: AgentScanRequest) => {
    console.log('🔍 Creating scan via agent:', agentId, data);
    return api.post(`/agents/${agentId}/scan`, data);
  },
  getScans: (agentId: string, limit: number = 50) => {
    console.log('📊 Fetching scans for agent:', agentId);
    return api.get(`/agents/${agentId}/scans?limit=${limit}`);
  },
  cancelScan: (agentId: string, scanId: string) => {
    console.log('⏹️ Cancelling scan on agent:', agentId, scanId);
    return api.post(`/agents/${agentId}/scans/${scanId}/cancel`);
  },
  getConnected: () => {
    console.log('🟢 Fetching connected agents');
    return api.get('/agents/connected');
  },
  checkUpdate: async (agentId: string) => {
    console.log('🔄 Checking for agent updates:', agentId);
    const response = await api.get(`/agents/${agentId}/update-check`);
    return response.data;
  },
  getUpdatesAvailable: async () => {
    console.log('🔄 Fetching agents with updates available');
    const response = await api.get('/agents/updates-available');
    return response.data;
  },
  getConfig: (agentId: string, os: 'windows' | 'linux' | 'darwin' = 'linux') => {
    console.log('📥 Downloading agent config:', agentId, 'for OS:', os);
    return api.get(`/agents/${agentId}/config?os=${os}`, { responseType: 'blob' });
  },
  getVersionConfig: () => {
    console.log('📋 Fetching agent version config');
    return api.get('/agents/version');
  },
  updateVersionConfig: (data: {
    latest_version: string;
    release_notes_url?: string;
    download_url_windows?: string;
    download_url_linux?: string;
    download_url_macos?: string;
    minimum_version?: string;
    update_required?: boolean;
    release_notes?: string;
  }) => {
    console.log('📝 Updating agent version config:', data);
    return api.put('/agents/version', data);
  },
  triggerClientReview: (agentId: string, options?: {
    project_id?: number;
    target_id?: number;
    language?: string;
    level?: number;
    enable_credentials?: boolean;
    enable_network?: boolean;
    enable_persistence?: boolean;
    enable_software?: boolean;
    enable_browser_sec?: boolean;
    enable_certs?: boolean;
    enable_ad?: boolean;
    categories?: string[];
    timing_mode?: 'now' | 'scheduled' | 'recurring';
    scheduled_at?: string;
    recurring?: {
      frequency: 'daily' | 'weekly' | 'monthly';
      day_of_week: number;
      day_of_month: number;
      time: string;
    };
  }) => {
    console.log('🔍 Triggering client review on agent:', agentId, options);
    return api.post(`/agents/${agentId}/client-review`, options || {});
  },

  // Client review schedule management
  getClientReviewSchedules: (agentId: string) => {
    return api.get(`/agents/${agentId}/client-review-schedules`);
  },
  listAllClientReviewSchedules: (projectId?: number) => {
    const params = projectId ? `?project_id=${projectId}` : '';
    return api.get(`/client-review-schedules${params}`);
  },
  updateClientReviewSchedule: (scheduleId: number, data: {
    enabled?: boolean;
    frequency?: string;
    day_of_week?: number;
    day_of_month?: number;
    time_of_day?: string;
  }) => {
    return api.put(`/client-review-schedules/${scheduleId}`, data);
  },
  deleteClientReviewSchedule: (scheduleId: number) => {
    return api.delete(`/client-review-schedules/${scheduleId}`);
  },
};
